import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import {
  NOTARY_COUNTRY_LANDINGS,
  getNotaryCountryLanding,
  type NotaryCountryLanding,
} from "@/content/notary-corpus/countries";
import { NOTARY_DOC_LANDINGS } from "@/content/notary-corpus/documents";

function faqsTh(c: NotaryCountryLanding) {
  return [
    { q: `เอกสารไทยที่จะใช้ใน${c.nameTh} ต้องรับรองอย่างไร?`, a: c.chainTh },
    {
      q: `${c.nameTh}รับ Apostille จากไทยได้หรือยัง?`,
      a: c.hagueParty
        ? `${c.nameTh}เป็นภาคีอนุสัญญากรุงเฮก 1961 อยู่แล้ว ส่วนประเทศไทยยื่นภาคยานุวัติสารเมื่อ 30 มิถุนายน 2026 และอนุสัญญาจะมีผลใช้บังคับกับไทยวันที่ 28 กุมภาพันธ์ 2027 ดังนั้นในช่วงก่อนวันดังกล่าว เอกสารไทยยังต้องผ่านนิติกรณ์กรมการกงสุลและรับรองต่อที่สถานทูตปลายทางตามเดิม (ที่มา: hcch.net และกรมการกงสุล กระทรวงการต่างประเทศ)`
        : `${c.nameTh}ยังไม่ได้เป็นภาคีอนุสัญญากรุงเฮก 1961 ดังนั้นแม้อนุสัญญาจะมีผลกับประเทศไทยในวันที่ 28 กุมภาพันธ์ 2027 เอกสารที่จะใช้ใน${c.nameTh}ก็ยังต้องใช้เส้นทางนิติกรณ์กรมการกงสุลและรับรองต่อที่สถานทูต${c.nameTh}ในไทย`,
    },
    { q: `หลังอนุสัญญามีผลกับไทยแล้ว ขั้นตอนจะเปลี่ยนอย่างไร?`, a: c.afterTh },
    { q: `ต้องแปลเอกสารเป็นภาษาอะไรสำหรับ${c.nameTh}?`, a: c.languageTh },
    { q: `เอกสารที่ส่งไป${c.nameTh}บ่อยที่สุดคืออะไร?`, a: c.topDocsTh.join(" · ") },
    { q: `จุดที่ทำให้เอกสารถูกปฏิเสธที่${c.nameTh}มีอะไรบ้าง?`, a: c.pitfallsTh.join(" · ") },
    {
      q: "ต้องมาลงนามต่อหน้าทนายเองไหม?",
      a: "ต้องลงนามต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารด้วยตนเอง พร้อมแสดงหนังสือเดินทางหรือบัตรประชาชนตัวจริง เอกสารที่ลงนามมาก่อนต้องลงนามใหม่ ส่วนการรับรองสำเนาถูกต้องต้องนำต้นฉบับมาแสดง",
    },
    {
      q: "ค่าธรรมเนียมคิดอย่างไร?",
      a: "ค่าบริการทนายคิดตามจำนวนฉบับและตราประทับ ค่าธรรมเนียมนิติกรณ์กรมการกงสุลและค่าธรรมเนียมสถานทูตปลายทางคิดตามอัตราทางการของหน่วยงานนั้นแยกจากค่าบริการ ขอใบเสนอราคาก่อนเริ่มงานได้ทุกครั้ง",
    },
    {
      q: "รับประกันว่าปลายทางจะรับเอกสารไหม?",
      a: "ไม่ การยอมรับเอกสารเป็นดุลพินิจของหน่วยงานปลายทาง เราตรวจรูปแบบ ถ้อยคำรับรอง ตัวสะกดชื่อตามหนังสือเดินทาง อายุเอกสาร และลำดับขั้นการรับรองก่อนดำเนินการ และแก้ไขใหม่โดยไม่คิดค่าบริการหากข้อผิดพลาดเกิดจากฝั่งเรา",
    },
  ];
}

export const Route = createFileRoute("/notary/country/$country")({
  loader: ({ params }) => {
    const c = getNotaryCountryLanding(params.country);
    if (!c) throw notFound();
    return { c };
  },
  component: NotaryCountryPageTH,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบประเทศ" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { c } = loaderData;
    const title = `รับรองเอกสารไป${c.nameTh} Notary Public 2026`;
    const description = `เอกสารไทยที่จะใช้ใน${c.nameTh}: ทนายรับรอง → แปล → นิติกรณ์กรมการกงสุล → สถานทูต${c.nameTh} พร้อมข้อกำหนดภาษา จุดพลาดบ่อย และผลของอนุสัญญา Apostille ที่จะมีผลกับไทย 28 ก.พ. 2027`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/notary/country/${params.country}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/notary/country/${params.country}` }],
        keywords: [
          `รับรองเอกสารไป${c.nameTh}`,
          `notary public ${c.nameTh}`,
          `หนังสือมอบอำนาจ ${c.nameTh}`,
          `นิติกรณ์ ${c.nameTh}`,
          `apostille ${c.nameTh}`,
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: `${site}/` },
              { name: "ทนาย Notary Public", url: `${site}/notary` },
              { name: `รับรองเอกสารไป${c.nameTh}`, url: `${site}/notary/country/${params.country}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqsTh(c))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/notary/country/${params.country}`)) },
      ],
    };
  },
});

function NotaryCountryPageTH() {
  const { c } = Route.useLoaderData();
  const faqs = faqsTh(c);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/notary" className="hover:underline">ทนาย Notary Public</a> <span>/</span>{" "}
          <span>{c.nameTh}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          รับรองเอกสารไป{c.nameTh} — ทนาย Notary Public + นิติกรณ์ + สถานทูต
        </h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">{c.chainTh}</p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">ขอใบเสนอราคาฟรี →</a>
          <a href="/notary/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">คำถามที่พบบ่อย Notary</a>
          <a href={`/en/notary/country/${c.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">English →</a>
        </div>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">สถานะอนุสัญญา Apostille — {c.nameTh}</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {c.hagueParty
              ? `${c.nameTh}เป็นภาคีอนุสัญญากรุงเฮก ค.ศ. 1961 อยู่แล้ว`
              : `${c.nameTh}ยังไม่ได้เป็นภาคีอนุสัญญากรุงเฮก ค.ศ. 1961`}{" "}
            สำหรับประเทศไทย ได้ยื่นภาคยานุวัติสารเมื่อวันที่ 30 มิถุนายน 2026 และอนุสัญญาจะมีผลใช้บังคับกับประเทศไทยในวันที่
            28 กุมภาพันธ์ 2027 หลังพ้นช่วงเวลาคัดค้าน 8 เดือน ดังนั้นในช่วงเวลาปัจจุบัน เอกสารไทยยังต้องใช้เส้นทางนิติกรณ์ตามปกติ
          </p>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{c.afterTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">เอกสารที่ส่งไป{c.nameTh}บ่อยที่สุด</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {c.topDocsTh.map((d: string, i: number) => <li key={i}>{d}</li>)}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">ข้อกำหนดด้านภาษาและการแปล</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.languageTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">จุดพลาดที่พบบ่อยกับ{c.nameTh}</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {c.pitfallsTh.map((d: string, i: number) => <li key={i}>{d}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">คำถามที่พบบ่อย — รับรองเอกสารไป{c.nameTh}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">รับรองเอกสารตามประเภท</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_DOC_LANDINGS.map((x) => (
              <a key={x.slug} href={`/notary/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleTh}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ประเทศปลายทางอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_COUNTRY_LANDINGS.filter((x) => x.slug !== c.slug).map((x) => (
              <a key={x.slug} href={`/notary/country/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.nameTh}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-6">
          <h2 className="mb-2 text-base font-bold">แหล่งอ้างอิง</h2>
          <ul className="list-disc space-y-1 pl-6 text-xs text-muted-foreground">
            {c.sources.map((s: { label: string; url: string }, i: number) => (
              <li key={i}>
                <a href={s.url} target="_blank" rel="noopener nofollow" className="hover:underline">{s.label}</a>
              </li>
            ))}
          </ul>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          กรอบวิชาชีพอ้างอิง: ข้อบังคับสภาทนายความว่าด้วยการขึ้นทะเบียนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร พ.ศ. 2551 ·
          ข้อมูลอนุสัญญากรุงเฮกอ้างอิงประกาศ HCCH วันที่ 30 มิถุนายน 2026 และกรมการกงสุล กระทรวงการต่างประเทศ —
          ค่าธรรมเนียม ระยะเวลา และข้อกำหนดของแต่ละประเทศอาจเปลี่ยนแปลง โปรดตรวจสอบกับหน่วยงานเจ้าของเรื่องก่อนดำเนินการทุกครั้ง
        </p>

        <PageGallery category="notary" seed="notary-country" locale="th" />
        <FaqSourceNote topic="notary" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
